import React, { useEffect } from 'react';

import { Maybe } from 'graphql/jsutils/Maybe';
import { useIntl } from 'react-intl';

import { DestructuredBurgerSvg } from 'components/svgs/destructured-burger';
import { ServiceMode } from 'generated/graphql-gateway';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';

import {
  MenuEmptyStateContainer,
  MenuEmptyStateImageWrapper,
  MenuEmptyStateMessage,
  MenuEmptyStateTitle,
} from './styled';

type MenuEmptyStateProps = {
  logWhenTrue?: boolean;
  logContext?: {
    activeDayParts: string[];
    showBurgersForBreakfast: boolean;
    serviceMode?: ServiceMode | null;
    isFetchedMenuEmpty: boolean;
    isPricingAndDependenciesSettled: boolean;
    storeId?: Maybe<string>;
    storePosVendor?: Maybe<string>;
  };
};

export const MenuEmptyState = ({ logWhenTrue = false, logContext }: MenuEmptyStateProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobileBreakpoint();
  const title = formatMessage({ id: 'menuEmptyScreenTitle' });
  const message = formatMessage({
    id: isMobile ? 'menuEmptyScreenMessage' : 'menuEmptyScreenDesktopMessage',
  });

  // this is for analytics purposes - runs once per screen view
  useEffect(() => {
    if (logWhenTrue) {
      logRBIEvent({
        name: CustomEventNames.EMPTY_MENU_SCREEN,
        type: EventTypes.Other,
      });
    }
  }, [logWhenTrue]);

  // this is for error tracking purposes
  useLogUserErrorMessage({
    logWhenTrue,
    message: 'Menu data is empty, displaying empty state',
    severity: LogUserErrorSeverity.Recoverable,
    context: logContext,
  });

  return (
    <MenuEmptyStateContainer>
      <MenuEmptyStateImageWrapper>
        <DestructuredBurgerSvg width={'full'} />
      </MenuEmptyStateImageWrapper>
      <MenuEmptyStateTitle>{title}</MenuEmptyStateTitle>
      <MenuEmptyStateMessage>{message}</MenuEmptyStateMessage>
    </MenuEmptyStateContainer>
  );
};
